// hooks.js
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getJSON, postJSON, patchJSON } from './api';

import { toPeriodsFromYYYYMM, formatDateTimeISO } from './utils/date';

export function useServicePeriods(tenant_id, user_key) {
    return useQuery({
        queryKey: ['service_periods', tenant_id, user_key],
        queryFn: () => getJSON(`/api/service/get_service_periods?tenant_id=${tenant_id}&user_key=${user_key}`),
        keepPreviousData: true,
        staleTime: 60_000,
        select: (res) => {
            const raw = Array.isArray(res?.list) ? res.list : Array.isArray(res) ? res : [];
            const rows = raw.map((v) => (typeof v === 'string' ? v : v?.date)).filter(Boolean);
            return toPeriodsFromYYYYMM(rows); // { years, monthsByYear, latestYear, latestMonth }
        },
    });
}

export function useServiceCount(tenant_id, user_key, name, year, month, page, size) {
    return useQuery({
        queryKey: ['service_count', tenant_id, user_key, name, year, month, page, size],
        queryFn: () => getJSON(`/api/service/get_service_count?tenant_id=${tenant_id}&user_key=${user_key}&name=${name}&year=${year}&month=${month}&page=${page - 1}&size=${size}`),
        keepPreviousData: true,
        select: (res) => res?.list ?? res ?? [],
        staleTime: 0,
        refetchOnMount: 'always',
        refetchOnWindowFocus: true,
    });
}

export function useServiceHistory(tenant_id, user_key, name, year, month, page, size) {
    return useQuery({
        queryKey: ['service_history', tenant_id, user_key, name, year, month, page, size],
        queryFn: () =>
            getJSON(`/api/service/get_service_history?tenant_id=${tenant_id}&user_key=${user_key}&name=${name}&year=${year}&month=${month}&page=${page - 1}&size=${size}`),
        keepPreviousData: true,
        select: (res) => ({
            items: res?.items ?? [],
            total: Number(res?.total ?? 0),
        }),
        staleTime: 0,
        refetchOnMount: 'always',
        refetchOnWindowFocus: true,
    });
}

export function useServiceChartData(tenant_id, user_key, year, month) {
    return useQuery({
        queryKey: ['service_chart_data', tenant_id, user_key, year, month],
        queryFn: () => getJSON(`/api/service/get_service_chart_data?tenant_id=${tenant_id}&user_key=${user_key}&year=${year}&month=${month}`),
        keepPreviousData: true,
        select: (res) => ({
            items: res?.items ?? [],
            total: Number(res?.total ?? 0),
        }),
        staleTime: 0,
        refetchOnMount: 'always',
        refetchOnWindowFocus: true,
    });
}

export function useSystemDashboardTenantCount() {
    return useQuery({
        queryKey: ['tenant_count'],
        queryFn: () => getJSON(`/api/system/tenant_count`),
        placeholderData: 0,
        staleTime: 30_000,
    });
}

export function useSystemDashboardPersonalCount() {
    return useQuery({
        queryKey: ['personal_count'],
        queryFn: () => getJSON(`/api/system/personal_count`),
        placeholderData: 0,
        staleTime: 30_000,
    });
}

export function useSystemDashboardUserCount(tenant_id) {
    return useQuery({
        queryKey: ['user_count', tenant_id],
        queryFn: () => getJSON(`/api/system/user_count?tenant_id=${tenant_id}`),
        placeholderData: 0,
        staleTime: 0,
        refetchOnMount: 'always',
        refetchOnWindowFocus: true,
    });
}

export function useSystemDashboardTenantList(name, page = 1, size = 10) {
    return useQuery({
        queryKey: ['tenant_list', name, page, size], // 캐시 키에 상태 포함
        queryFn: () => getJSON(`/api/system/tenant_list?name=${name}&page=${page - 1}&size=${size}`),
        keepPreviousData: true,
        select: (res) => ({
            items: res?.items ?? [],
            total: Number(res?.total ?? 0),
        }),
    });
}

export function useSystemDashboardTenantDetail(id, enabled = !!id) {
    return useQuery({
        queryKey: ['tenant_detail', id],
        queryFn: () => getJSON(`/api/system/tenants/${id}`),
        enabled,
    });
}

export function useSystemDashboardTenantStatus() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, status }) => patchJSON(`/api/system/tenants/${id}/status`, { status }),
        onMutate: async ({ id, status }) => {
            // 목록 캐시 낙관적 업데이트
            const keys = qc
                .getQueryCache()
                .findAll({ queryKey: ['tenant_list'] })
                .map((e) => e.queryKey);
            const snapshots = [];
            for (const key of keys) {
                await qc.cancelQueries({ queryKey: key });
                const prev = qc.getQueryData(key);
                snapshots.push([key, prev]);
                if (prev?.items) {
                    const next = {
                        ...prev,
                        items: prev.items.map((it) => (it.id === id ? { ...it, status } : it)),
                    };
                    qc.setQueryData(key, next);
                }
            }
            return { snapshots };
        },
        onError: (_err, _vars, ctx) => {
            // 롤백
            ctx?.snapshots?.forEach(([key, prev]) => {
                qc.setQueryData(key, prev);
            });
        },
        onSettled: () => {
            // 실제 값 동기화
            qc.invalidateQueries({ queryKey: ['tenant_list'] });
            qc.invalidateQueries({ queryKey: ['tenant_detail'] });
            qc.invalidateQueries({ queryKey: ['tenant_count'] });
            qc.invalidateQueries({ queryKey: ['personal_count'] });
        },
    });
}

export function useSystemDashboardTenantUsage(name, year, month, page = 1, size = 10) {
    return useQuery({
        queryKey: ['usage_per_tenant', name, year, month, page, size], // 캐시 키에 상태 포함
        queryFn: () => getJSON(`/api/system/usage_per_tenant/${year}/${month}?name=${name}&page=${page - 1}&size=${size}`),
        keepPreviousData: true,
        select: (res) => {
            const items = res?.items ?? [];
            const total = Number(res?.total) || 0;
            return {
                total,
                items: items.map((r) => ({
                    tenant_key: r.tenant_key,
                    name: r.tenant,
                    count: Number(r.c) || 0,
                    traffic: Number(r.t) || 0,
                    storage: Number(r.s) || 0,
                    cost: Number(r.monthly_charge) || 0,
                    type: Number(r.p) || 0,
                })),
            };
        },
        // (rows ?? []).map((r) => ({
        //     tenant_key: r.tenant_key,
        //     name: r.tenant,
        //     count: Number(r.c) || 0,
        //     traffic: Number(r.t) || 0,
        //     storage: Number(r.s) || 0,
        //     cost: r.monthly_charge || 0,
        //     type: Number(r.p) || 0,
        // })),
    });
}

export function useSystemDashboardServiceUsage(name, year, month, page = 1, size = 10) {
    return useQuery({
        queryKey: ['usage_per_service', name, year, month, page, size], // 캐시 키에 상태 포함
        queryFn: () => getJSON(`/api/system/usage_per_service/${year}/${month}?name=${name}&page=${page - 1}&size=${size}`),
        keepPreviousData: true,
        select: (rows) =>
            (rows ?? []).map((r) => ({
                model_key: r.model_key,
                name: r.model_name,
                t_count: Number(r.tc) || 0,
                p_count: Number(r.pc) || 0,
                traffic: Number(r.t) || 0,
                storage: Number(r.s) || 0,
            })),
    });
}

export function useSystemDashboardServiceHistory(field, keyword, page = 1, size = 10) {
    return useQuery({
        queryKey: ['system_service_history', field, keyword, page, size], // 캐시 키에 상태 포함
        queryFn: () =>
            getJSON(`/api/system/service_history?field=${field}&keyword=${encodeURIComponent(keyword)}&page=${page - 1}&size=${size}`),
        keepPreviousData: true,
        select: (res) => ({
            items: res?.items ?? [],
            total: Number(res?.total ?? 0),
        }),
        staleTime: 0,
        refetchOnMount: 'always',
        refetchOnWindowFocus: true,
    });
}

export function useServiceListPerUser(tenant_key, name, page = 1, size = 10) {
    return useQuery({
        queryKey: ['service_list_per_user', tenant_key, name, page, size], // 캐시 키에 상태 포함
        queryFn: () => getJSON(`/api/system/service_list_per_user/${tenant_key}?name=${name}&page=${page - 1}&size=${size}`),
        keepPreviousData: true,
    });
}

export function useAccountInfo(tenant_key, user_key) {
    return useQuery({
        queryKey: ['get_account_info', tenant_key, user_key], // 캐시 키에 상태 포함
        queryFn: () => getJSON(`/api/service/account?tenant_key=${tenant_key}&user_key=${user_key}`),
        keepPreviousData: true,
    });
}

export function useUpdatePassword() {
    return useMutation({
        mutationFn: (payload) => postJSON('/api/service/password', payload),
    });
}

export function useTenantUsers(tenant_key, name, status, page = 1, size = 10) {
    return useQuery({
        queryKey: ['tenant_users', tenant_key, name, status, page, size], // 캐시 키에 상태 포함
        queryFn: () => getJSON(`/api/service/tenant_users/${tenant_key}?name=${name}&status=${status}&page=${page - 1}&size=${size}`),
        keepPreviousData: true,
        select: (res) => ({
            items: (res?.items ?? []).map((u) => ({
                ...u,
                created_at: formatDateTimeISO(u.created_at),
                updated_at: formatDateTimeISO(u.updated_at),
                last_login: formatDateTimeISO(u.last_login_at),
            })),
            total: Number(res?.total ?? 0),
        }),
        enabled: !!tenant_key,
        staleTime: 30_000,
    });
}

export function useServicePrice(tenant_key, user_key, year, month) {
    return useQuery({
        queryKey: ['service_price', tenant_key, user_key, year, month], // 캐시 키에 상태 포함
        queryFn: () => getJSON(`/api/service/price/${tenant_key}?user_id=${user_key}&year=${year}&month=${month}`),
        enabled: !!tenant_key,
        staleTime: 0,
        refetchOnMount: 'always',
        refetchOnWindowFocus: true,
    });
}
